import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import GoogleLoginButton from '../GoogleLoginButton';
import axios from 'axios';
import './Login.scss';

const Login = () => {
  const [currentView, setCurrentView] = useState('initial'); // 'initial', 'email', 'code'
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  const [systemConfig, setSystemConfig] = useState(null);
  const navigate = useNavigate();
  const { loginWithCode } = useAuth();
  const [searchParams] = useSearchParams();
  const { t } = useI18n();

  useEffect(() => {
    // 从 URL 参数获取推荐码
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      fetchReferralInfo(refCode);
      // 只有在有推荐码时才获取系统配置（注册奖励信息）
      fetchSystemConfig();
    }
  }, [searchParams]); // 添加 searchParams 依赖以正确获取 ref 参数

  const fetchReferralInfo = async (code) => {
    try {
      const response = await axios.get(`/referral/info/${code}`);
      if (response.data.success) {
        setReferralInfo(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch referral info:', error);
    }
  };

  const fetchSystemConfig = async () => {
    try {
      const response = await axios.get('/system/config/registration-bonus');
      if (response.data.success) {
        setSystemConfig(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch system config:', error);
    }
  };

  // 新的Google登录成功处理函数
  const handleNewGoogleLoginSuccess = async (result) => {
    try {
      setIsLoading(true);
      setError('');
      
      const { token, user } = result;
      
      // 保存 token 和用户信息
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // 设置 axios 默认 header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      console.log('Google login successful, user:', user.email);
      
      // 导航到 dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Handle Google login success result failed:', error);
      setError(t('login.loginProcessingFailed') || '登录处理失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  // 新的Google登录错误处理函数
  const handleNewGoogleLoginError = (error) => {
    console.error('Google login error:', error);
    setError(error.message || t('login.googleLoginFailed') || 'Google登录失败，请重试');
    setIsLoading(false);
  };


  
  const handleResendCode = async () => {
    if (resendTimer > 0) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post('/auth/login/send-code', {
        email
      });
      
      const data = response.data;
      
      if (data.success) {
        setResendTimer(60);
        
        // 启动倒计时
        const timer = setInterval(() => {
          setResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(data.message || t('login.resendCodeFailed') || 'Failed to resend verification code');
      }
    } catch (error) {
      console.error('Resend code error:', error);
      setError(t('login.resendCodeFailed') || 'Failed to resend verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailContinue = () => {
    setCurrentView('email');
  };

  const handleSendCode = async () => {
    if (!email) {
      setError(t('login.pleaseEnterEmail') || 'Please enter your email');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const requestData = { email };
      if (referralCode) {
        requestData.referralCode = referralCode;
      }
      
      const response = await axios.post('/auth/login/send-code', requestData);
      
      const data = response.data;
      
      if (data.success) {
        setCurrentView('code');
        setResendTimer(60);
        
        // 启动倒计时
        const timer = setInterval(() => {
          setResendTimer(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(data.message || 'Failed to send verification code');
      }
      
    } catch (error) {
      console.error('Send code error:', error);
      setError('Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (!verificationCode) {
      setError('Please enter verification code');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      const result = await loginWithCode(email, verificationCode, referralCode);
      
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Login failed, please check verification code');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentView === 'email') {
      setCurrentView('initial');
    } else if (currentView === 'code') {
      setCurrentView('email');
    }
    setError('');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <div className="logo">
            <img src="/logo.png" alt="Unlike raw API calls, our optimized Gemini interface provides a zero-config experience for creators." />
          </div>
          <h2>{t('login.title') || 'Sign In'}</h2>
          <p>{t('login.signInToAccount') || 'Sign in to your account'}</p>
          
          {/* 分享邀请提示信息 */}
          {referralCode && referralInfo && (
            <div className="referral-info">
              <div className="referral-message">
                <span className="referral-text">
                  {t('login.referralInvitation', {
                    referrerName: referralInfo.referrerName,
                    bonus: systemConfig?.newUserBonus || 0
                  })}
                </span>
              </div>
            </div>
          )}
          
          {!referralCode && (
            <div className="trial-info">
              <span>{t('login.freeTrialAvailable') || 'Free trial available after login'}</span>
            </div>
          )}
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {currentView === 'initial' && (
          <div className="modal-body">
            {/* 使用新的Google登录按钮组件 */}
            <GoogleLoginButton
              onSuccess={handleNewGoogleLoginSuccess}
              onError={handleNewGoogleLoginError}
              referralCode={referralCode}
              disabled={isLoading}
              className="google-btn"
              text={t('login.signInWithGoogle')}
              returnUrl="/"
            />
            
            <button className="email-btn" onClick={handleEmailContinue}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                <path fill="none" d="M0 0h24v24H0z"/>
                <path d="M3 3h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm17 4.238l-7.928 7.1L4 7.216V19h16V7.238zM4.511 5l7.55 6.662L19.502 5H4.511z" fill="rgba(255,255,255,1)"/>
              </svg>
              {t('login.continueWithEmail')}
            </button>
          </div>
        )}
        
        {currentView === 'email' && (
          <div className="modal-body">
            <p className="enter-email-text">{t('login.enterYourEmail')}</p>
            <div className="email-form">
              <label htmlFor="email">{t('login.email')}</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                placeholder="m@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button className="send-code-btn" onClick={handleSendCode} disabled={isLoading}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                  <path fill="none" d="M0 0h24v24H0z"/>
                  <path d="M3 3h18a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm17 4.238l-7.928 7.1L4 7.216V19h16V7.238zM4.511 5l7.55 6.662L19.502 5H4.511z" fill="rgba(255,255,255,1)"/>
                </svg>
                {isLoading ? t('login.sending') : t('login.sendVerificationCode')}
              </button>
            </div>
            <button className="back-btn" onClick={handleBack}>{t('common.back')}</button>
          </div>
        )}
        
        {currentView === 'code' && (
          <div className="modal-body">
            <p className="enter-email-text">{t('login.enterYourEmail')}</p>
            <div className="email-form">
              <label htmlFor="email-display">{t('login.email')}</label>
              <input type="email" id="email-display" name="email-display" value={email} disabled />
              
              <div className="verification-code-container">
                <label htmlFor="verification-code">{t('login.verificationCode')}</label>
                <button 
                  type="button"
                  onClick={handleResendCode}
                  className="resend-link"
                  disabled={resendTimer > 0 || isLoading}
                >
                  {resendTimer > 0 ? `${t('login.resendIn')} ${resendTimer}s` : t('login.resend')}
                </button>
              </div>
              <input 
                type="text" 
                id="verification-code" 
                name="verification-code" 
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
              
              <button className="signin-btn" onClick={handleSignIn} disabled={isLoading}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                  <path d="M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm0 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm-1 4h2v6h-2V8zm0 8h2v2h-2v-2z" fill="rgba(255,255,255,1)"/>
                </svg>
                {isLoading ? t('login.signingIn') : t('login.signIn')}
              </button>
            </div>
            <button className="back-btn-code" onClick={handleBack}>{t('common.back')}</button>
          </div>
        )}
        
        <div className="modal-footer">
          <p>{t('login.byContining')} <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="link-button">{t('login.privacyPolicy')}</a></p>
        </div>
      </div>
    </div>
  );
};

export default Login;
